import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '../api/apiClient';
import { DesafioNuevaPassword, SesionAdmin } from '../types/identidad';

interface RespuestaTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiraEnSegundos: number;
}

type RespuestaLogin = RespuestaTokens | DesafioNuevaPassword;

/**
 * Decodifica el payload de un JWT (sin verificar firma — la verificación real
 * la hace el backend/Cognito en cada petición protegida). Solo se usa para leer
 * los claims necesarios para poblar la sesión local (actorId, usuario, rol).
 *
 * cognito:groups viene como array real en el JWT crudo (a diferencia de como lo
 * entrega el Cognito Authorizer de API Gateway al Lambda — ahí llega como string con
 * corchetes, ver backend-api/src/shared/auth.ts — eso es un detalle de API Gateway,
 * no del token en sí).
 */
function decodificarClaims(
  idToken: string,
): { sub: string; 'cognito:username'?: string; 'cognito:groups'?: string[] } {
  const payload = idToken.split('.')[1] ?? '';
  return JSON.parse(atob(payload));
}

function tokensASesion(tokens: RespuestaTokens, usuarioFallback: string): SesionAdmin {
  const claims = decodificarClaims(tokens.idToken);
  return {
    ...tokens,
    actorId: claims.sub,
    usuario: claims['cognito:username'] ?? usuarioFallback,
    rol: claims['cognito:groups']?.includes('Administrador') ? 'Administrador' : 'Empleado',
  };
}

function esDesafio(respuesta: RespuestaLogin): respuesta is DesafioNuevaPassword {
  return 'challenge' in respuesta;
}

/** Login puede devolver una sesión lista para usar, o el desafío NEW_PASSWORD_REQUIRED
 * (primer login / cuenta recién creada) — el llamador debe chequear cuál de los dos es. */
export function useLogin() {
  return useMutation({
    mutationFn: async ({
      usuario,
      password,
    }: {
      usuario: string;
      password: string;
    }): Promise<SesionAdmin | DesafioNuevaPassword> => {
      const respuesta = await apiFetch<RespuestaLogin>('/auth/login', {
        method: 'POST',
        body: { usuario, password },
      });
      if (esDesafio(respuesta)) return respuesta;
      return tokensASesion(respuesta, usuario);
    },
  });
}

/** Responde al desafío NEW_PASSWORD_REQUIRED con la nueva contraseña — devuelve una
 * sesión lista para usar, igual que un login exitoso. */
export function useResponderNuevaPassword() {
  return useMutation({
    mutationFn: async (datos: { usuario: string; session: string; nuevaPassword: string }): Promise<SesionAdmin> => {
      const respuesta = await apiFetch<RespuestaTokens>('/auth/nueva-password', {
        method: 'POST',
        body: datos,
      });
      return tokensASesion(respuesta, datos.usuario);
    },
  });
}

/** Solicita el código de verificación para restablecer la contraseña (por correo). */
export function useOlvidePassword() {
  return useMutation({
    mutationFn: async (datos: { usuario: string }) =>
      apiFetch<{ mensaje: string }>('/auth/olvide-password', { method: 'POST', body: datos }),
  });
}

/** Confirma el restablecimiento con el código recibido por correo. */
export function useConfirmarPassword() {
  return useMutation({
    mutationFn: async (datos: { usuario: string; codigo: string; nuevaPassword: string }) =>
      apiFetch<void>('/auth/confirmar-password', { method: 'POST', body: datos }),
  });
}

/** Cambia la contraseña de la sesión activa (requiere la contraseña actual). */
export function useCambiarPassword() {
  return useMutation({
    mutationFn: async (datos: { passwordActual: string; passwordNueva: string }) =>
      apiFetch<void>('/auth/cambiar-password', { method: 'POST', body: datos, autenticado: true }),
  });
}
